import { TreeDataNode, TreeProps } from "antd";
import { Key } from "react";

export const findNode = <T extends { key: string; children: T[] }>(nodes: T[], nodeKey: string): T | undefined => {
  for (const node of nodes) {
    if (node.key === nodeKey) {
      return node;
    }
    const childNode = findNode(node.children, nodeKey);
    if (childNode) return childNode;
  }
  return undefined;
};

export const findParentNode =
  <T extends { key: string; children: T[] }>(nodes: T[], nodeKey: string, currentParentNode: T | null | undefined = null): T | null | undefined => {
    for (const node of nodes) {
      if (node.key === nodeKey) {
        return currentParentNode;
      }

      const parentNode = findParentNode(node.children, nodeKey, node);
      if (parentNode) return parentNode;
    }
    return undefined;
  };

export const populateNodeLookup = <T extends { key: string; children: T[] }>(
  nodes: T[], nodeKeys: string[] | undefined = undefined, nodeLookup: Map<string, T> = new Map<string, T>(),
): Map<string, T> => {
  for (const node of nodes) {
    if (nodeKeys === undefined || nodeKeys.includes(node.key)) {
      nodeLookup.set(node.key, node);
    }
    populateNodeLookup(node.children, nodeKeys, nodeLookup);
  }
  return nodeLookup;
};

export function traverse<T extends { children: T[] }>(nodes: T[], consumer: (node: T, parent?: T) => void, parent?: T) {
  nodes.forEach((node) => {
    consumer(node, parent);
    traverse(node.children, consumer, node);
  });
}

// taken from the drag&drop example of antd's tree component: https://ant.design/components/tree
export const onTreeDrop = <T extends TreeDataNode>(viewTree: T[], info: Parameters<NonNullable<TreeProps["onDrop"]>>[0]): T[] => {
  const dropKey = info.node.key;
  const dragKey = info.dragNode.key;
  const dropPos = info.node.pos.split("-");
  const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

  const loop = (data: T[], key: Key, callback: (node: T, i: number, data: T[]) => void) => {
    for (let i = 0; i < data.length; i++) {
      if (data[i].key === key) {
        return callback(data[i], i, data);
      }
      if (data[i].children) {
        loop(data[i].children as T[], key, callback);
      }
    }
  };

  const data = [...viewTree];

  // Find dragObject
  let dragObj: T;
  loop(data, dragKey, (item, index, arr) => {
    arr.splice(index, 1);
    dragObj = item;
  });

  if (!info.dropToGap) {
    // Drop on the content
    loop(data, dropKey, (item) => {
      item.children = item.children || [];
      // where to insert. New item was inserted to the start of the array in this example, but can be anywhere
      item.children.unshift(dragObj);
    });
  } else if (
    // content of this function was copied from the antd example (see link above), so don't change it just to please ESLint
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((info.node as any).props.children || []).length > 0 // Has children
    // content of this function was copied from the antd example (see link above), so don't change it just to please ESLint
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    && (info.node as any).props.expanded // Is expanded
    && dropPosition === 1 // On the bottom gap
  ) {
    loop(data, dropKey, (item) => {
      item.children = item.children || [];
      // where to insert. New item was inserted to the start of the array in this example, but can be anywhere
      item.children.unshift(dragObj);
      // in previous version, we use item.children.push(dragObj) to insert the
      // item to the tail of the children
    });
  } else {
    let ar: T[] = [];
    let i: number;
    loop(data, dropKey, (_item, index, arr) => {
      ar = arr;
      i = index;
    });
    if (dropPosition === -1) {
      // content of this function was copied from the antd example (see link above), so don't change it just to please ESLint
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ar.splice(i!, 0, dragObj!);
    } else {
      // content of this function was copied from the antd example (see link above), so don't change it just to please ESLint
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ar.splice(i! + 1, 0, dragObj!);
    }
  }

  return data;
};

export const onTreeMultiDrop = <T extends TreeDataNode>(viewTree: T[], info: Parameters<NonNullable<TreeProps["onDrop"]>>[0], selectedKeys: string[]): T[] => {
  const dropKey = info.node.key;
  const dragKey = info.dragNode.key;
  const dragKeyStr = dragKey.toString();

  // move either selected nodes or the dragged node
  const moveNodeKeys = selectedKeys.includes(dragKeyStr) ? selectedKeys : [dragKeyStr];

  const dragKeys = new Set(moveNodeKeys);
  const dropPos = info.node.pos.split("-");
  const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

  const findDragObjects = (items: T[], skipNodeKeys: Key[]): T[] => {
    const result: T[] = [];
    const spliceIndexes = [];
    for (let i = 0; i < items.length; i++) {
      const itemKey = items[i].key;
      if (dragKeys.has(itemKey as string) && !skipNodeKeys.includes(itemKey)) {
        result.push(items[i]);
        spliceIndexes.push(i);
      } else if (items[i].children) {
        result.push(...findDragObjects(items[i].children as T[], skipNodeKeys));
      }
    }

    // remove items
    spliceIndexes.reverse().forEach((index) => {
      items.splice(index, 1);
    });
    return result;
  };

  const findNode = (
    data: T[],
    key: Key,
    callback: (nodeIndex: number, nodes: T[], pathFromRoot: Key[]) => void,
    pathFromRoot: Key[] = [],
  ) => {
    for (let i = 0; i < data.length; i++) {
      const node = data[i];
      if (node.key === key) {
        return callback(i, data, pathFromRoot);
      }
      if (node.children) {
        const nodeChildren = data[i].children as T[];
        findNode(nodeChildren, key, callback, [...pathFromRoot, node.key]);
      }
    }
  };

  const data = [...viewTree];
  findNode(data, dropKey, (dropNodeIndex, nodes, pathFromRoot) => {
    const dropNode = nodes[dropNodeIndex];

    // Find dragObjects
    const dragObjects: T[] = findDragObjects(data, [...pathFromRoot, dropNode.key]);

    if (!info.dropToGap) {
      // Drop on the content
      dropNode.children = dropNode.children || [];
      // where to insert. New item was inserted to the start of the array in this example, but can be anywhere
      dropNode.children.unshift(...dragObjects);
    } else if (
      // content of this function was copied from the antd example (see link above), so don't change it just to please ESLint
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((info.node as any).props.children || []).length > 0 // Has children
      // content of this function was copied from the antd example (see link above), so don't change it just to please ESLint
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      && (info.node as any).props.expanded // Is expanded
      && dropPosition === 1 // On the bottom gap
    ) {
      // Drop on the content
      dropNode.children = dropNode.children || [];
      // where to insert. New item was inserted to the start of the array in this example, but can be anywhere
      dropNode.children.unshift(...dragObjects);
    } else {
      if (dropPosition === -1)
        nodes.splice(dropNodeIndex, 0, ...dragObjects);
      else
        nodes.splice(dropNodeIndex + 1, 0, ...dragObjects);
    }
  });

  return data;
};

enum SelectState { START, PROCESS, FINISHED }

interface Result {
  keys: string[];
  state: SelectState;
}

const selectedKeys = <T extends TreeDataNode>(nodes: T[], boundingKeys: Set<string>, state: SelectState, expandedKeys: Set<string>): Result => {
  const result: string[] = [];
  let workState = state;
  nodes.forEach((node) => {
    const nodeKey = node.key as string;
    if (workState !== SelectState.FINISHED) {
      if (boundingKeys.has(nodeKey)) {
        result.push(nodeKey);
        workState = workState === SelectState.START ? SelectState.PROCESS : SelectState.FINISHED;
      } else if (workState === SelectState.PROCESS)
        result.push(nodeKey);
      // check children
      if (workState !== SelectState.FINISHED && node.children && expandedKeys.has(nodeKey)) {
        const childrenResult = selectedKeys(node.children, boundingKeys, workState, expandedKeys);
        workState = childrenResult.state;
        result.push(...childrenResult.keys);
      }
    }
  });
  return { keys: result, state: workState };
};

export const getNodeInterval = <T extends TreeDataNode>(viewTree: T[], boundingKeys: Set<string>, expandedKeys: string[]): string[] => {
  return selectedKeys([...viewTree], boundingKeys, SelectState.START, new Set(expandedKeys)).keys;
};
